import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useFlags } from "@/lib/flags";
import { getSchoolUrl } from "@/lib/slug";
import { ArrowLeft, MapPin, Star } from "lucide-react";

const TYPE_LABELS = { negeri: "Negeri", swasta: "Swasta", internasional: "Internasional", islam: "Islam", katolik: "Katolik", kristen: "Kristen" };

export default function BrandDetail() {
  const { school_reviews_enabled: reviewsOn } = useFlags();
  const { brandId } = useParams();
  const [schools, setSchools] = useState(null);

  useEffect(() => {
    api.get(`/brands/${brandId}/schools`).then((r) => setSchools(r.data)).catch(() => setSchools([]));
  }, [brandId]);

  const displayName = brandId?.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()) || "";

  return (
    <div data-testid="brand-detail-page" className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
      <Link to="/brands" className="text-sm text-[#4B5563] hover:text-[#E07A5F] inline-flex items-center gap-1.5" data-testid="brand-detail-back">
        <ArrowLeft size={14} /> Semua grup
      </Link>
      <div className="mt-4 text-xs uppercase tracking-widest text-[#4B5563] font-mono">{brandId}</div>
      <h1 className="font-serif-display text-4xl lg:text-6xl leading-tight text-[#1F2937] mt-2">{displayName}</h1>
      <p className="mt-3 text-[#4B5563]">Semua cabang dalam grup ini.</p>

      {schools === null ? (
        <div className="mt-12 text-center text-[#4B5563]">Memuat cabang…</div>
      ) : schools.length === 0 ? (
        <div className="mt-12 text-center text-[#4B5563] p-10 rounded-2xl border border-dashed border-[#EAE7E0]">
          Belum ada cabang yang terdaftar untuk grup ini.
        </div>
      ) : (
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {schools.map((s, i) => (
            <Link
              key={s.school_id}
              to={getSchoolUrl(s)}
              data-testid={`brand-branch-${i}`}
              className="group bg-white border border-[#EAE7E0] rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-[0_12px_32px_rgba(0,0,0,0.06)] transition-all duration-300 flex flex-col"
            >
              <div className="aspect-[4/3] bg-[#F3F1ED] overflow-hidden">
                {s.image_url && <img src={s.image_url} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
              </div>
              <div className="p-5 flex-1 flex flex-col">
                {s.branch_name && (
                  <div className="text-xs uppercase tracking-widest text-[#E07A5F] font-semibold">Cabang {s.branch_name}</div>
                )}
                <div className="font-serif-display text-lg text-[#1F2937] leading-tight mt-1">{s.name}</div>
                <div className="mt-1.5 text-xs text-[#4B5563] inline-flex items-center gap-1">
                  <MapPin size={11} /> {s.city} · {TYPE_LABELS[s.school_type] || s.school_type}
                </div>
                {s.education_levels?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {s.education_levels.map((l) => (
                      <span key={l} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-[#EEF2FF] text-[#1E3A8A] border border-[#DBEAFE]">{l}</span>
                    ))}
                  </div>
                )}
                <div className="mt-auto pt-3 flex items-center justify-between text-sm">
                  {reviewsOn ? <span className="inline-flex items-center gap-1 text-[#1F2937]">
                    <Star size={13} className="fill-[#F59E0B] stroke-[#F59E0B]" /> {(s.avg_rating || 0).toFixed(1)}
                    <span className="text-xs text-[#4B5563] ml-1">· {s.review_count || 0} review</span>
                  </span> : <span />}
                  {s.accreditation_grade && (
                    <span className={
                      "px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest border " +
                      (s.accreditation_grade === "A" ? "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]" :
                       s.accreditation_grade === "B" ? "bg-[#EFF6FF] text-[#1E3A8A] border-[#DBEAFE]" :
                       "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]")
                    }>
                      Akr. {s.accreditation_grade}
                    </span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
